import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';

import { adminSession, type UserRow } from '@/lib/api';
import { api } from '../../../convex/functions/_generated/api';
import { UserActionsMenu } from '@/components/UserActionsMenu';
import { PLAN_LABELS, currentChoice } from '@/components/PlanPopover';

// "New user" routes to the full-profile builder at /dashboard/users/new
// — the auth-only flow was dropped in favour of one creation surface
// that ships a complete profile (auth + photos + bio + publish).

/**
 * Users listing page. Search by email substring OR exact user_id
 * (uuid). Per-row 3-dot menu surfaces every admin action via the
 * shared UserActionsMenu component — the same menu is used on the
 * UserDetail page header so behaviour stays in lockstep.
 */
export function UsersPage() {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  /**
   * Synthetic = created through the /admin/v1 API. Defaults to "all"
   * rather than hiding them, so nobody is ever unaware that seeded rows
   * exist — but "real" is the one that matters once a seeding run has
   * added hundreds and drowned the genuine signups this table exists for.
   */
  const [syntheticFilter, setSyntheticFilter] =
    useState<'all' | 'synthetic' | 'real'>('all');
  const [error, setError] = useState<string | null>(null);

  const token = adminSession.getToken();
  const data = useQuery(
    api.admin.users.listUsers,
    token ? { token, search, page, pageSize, syntheticFilter } : 'skip',
  );

  const loading = data === undefined;
  const rows = (data?.users ?? []) as UserRow[];
  const total = data?.total ?? 0;

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  // Reactivity replaces the optimistic patch — useQuery re-subscribes
  // and re-renders the list as soon as the mutation lands. The menu
  // still calls onChange (legacy shape), so we accept it as a no-op.
  const patchRow = (_updated: UserRow) => {
    /* no-op — useQuery auto-refreshes */
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, gap: 16, flexWrap: 'wrap' }}>
        <h2 className="serif" style={{ fontSize: 28, fontWeight: 500, fontStyle: 'italic', margin: 0 }}>
          Users
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}>
          <Link to="/dashboard/users/new" style={{ ...searchBtn, textDecoration: 'none' }}>
            + New user
          </Link>
          <form onSubmit={submitSearch} style={{ display: 'flex', gap: 8, minWidth: 0, maxWidth: '100%' }}>
            <input
              type="text"
              placeholder="Search by email or user id…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={searchInputStyle}
            />
            <button type="submit" style={searchBtn}>Search</button>
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  setSearch('');
                  setPage(1);
                }}
                style={clearBtn}
              >
                Clear
              </button>
            )}
          </form>
          <select
            value={syntheticFilter}
            onChange={(e) => {
              setSyntheticFilter(e.target.value as 'all' | 'synthetic' | 'real');
              setPage(1);
            }}
            style={searchInputStyle}
            title="Filter by how the account was created"
          >
            <option value="all">All accounts</option>
            <option value="real">Real signups only</option>
            <option value="synthetic">Synthetic (API-created) only</option>
          </select>
        </div>
      </div>

      {error && (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--line)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--bg)' }}>
              <Th>Email</Th>
              <Th>Display name</Th>
              <Th>Auth</Th>
              <Th>Profile</Th>
              <Th>Verified</Th>
              <Th>Consent</Th>
              <Th>Status</Th>
              <Th>Plan</Th>
              <Th>Joined</Th>
              <Th>Last seen</Th>
              <Th>{' '}</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} style={emptyCell}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={11} style={emptyCell}>{search
                  ? `No users match "${search}".`
                  : syntheticFilter === 'synthetic'
                    ? 'No synthetic accounts yet.'
                    : syntheticFilter === 'real'
                      ? 'No real signups yet.'
                      : 'No users yet.'}</td></tr>
            ) : (
              rows.map((u) => (
                <UserTableRow
                  key={u.id}
                  user={u}
                  onChange={patchRow}
                  onError={setError}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 13, color: 'var(--ink-3)' }}>
          <div>
            {total} {total === 1 ? 'user' : 'users'} · page {page} of {totalPages}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} style={pageBtn}>← Prev</button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={pageBtn}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** One row + its row-level menu trigger. Pulled out so the row owns its
 *  own UserActionsMenu instance — keeps a row's modals scoped to its
 *  own user without prop-drilling through the table. */
function UserTableRow({
  user, onChange, onError,
}: {
  user: UserRow;
  onChange: (u: UserRow) => void;
  onError: (msg: string) => void;
}) {
  const navigate = useNavigate();
  return (
    <tr style={{ borderTop: '1px solid var(--line)' }}>
      <Td mono>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Link to={`/dashboard/users/${user.id}`} style={emailLink}>{user.email}</Link>
          {user.synthetic && (
            <span
              style={syntheticBadge}
              title={
                `Created via the admin API on ` +
                `${new Date(user.synthetic.created_at).toLocaleString()}` +
                (user.synthetic.note ? ` — ${user.synthetic.note}` : '')
              }
            >
              synthetic
            </span>
          )}
        </span>
      </Td>
      <Td>
        {user.display_name
          ? <Link to={`/dashboard/users/${user.id}`} style={emailLink}>{user.display_name}</Link>
          : <span style={{ color: 'var(--ink-3)' }}>—</span>}
      </Td>
      <Td><AuthBadge method={user.auth_method} /></Td>
      <Td>{user.profile_complete ? '✓' : <span style={{ color: 'var(--ink-3)' }}>pending</span>}</Td>
      <Td>{user.email_verified ? '✓' : <span style={{ color: 'var(--ink-3)' }}>no</span>}</Td>
      <Td>{user.terms_accepted ? '✓' : <span style={{ color: 'var(--ink-3)' }}>no</span>}</Td>
      <Td><StatusBadge status={user.status} /></Td>
      <Td><PlanBadge user={user} /></Td>
      <Td mono>{formatDate(user.created_at)}</Td>
      <Td mono>{lastSeenLabel(user)}</Td>
      <Td>
        <UserActionsMenu
          user={user}
          onChange={onChange}
          onError={onError}
          onViewProfile={() => navigate(`/dashboard/users/${user.id}`)}
          trigger={({ onClick, isOpen }) => (
            <button
              type="button"
              onClick={(e) => onClick(e.currentTarget)}
              aria-label="Row actions"
              style={{
                background: isOpen ? 'var(--bg)' : 'transparent',
                border: 'none',
                borderRadius: 8,
                padding: '4px 8px',
                cursor: 'pointer',
                color: 'var(--ink-2)',
                fontSize: 18,
                lineHeight: 1,
                fontFamily: 'var(--font-ui)',
              }}
            >
              ⋯
            </button>
          )}
        />
      </Td>
    </tr>
  );
}

// ───── presentational helpers ─────────────────────────────────────────────

function AuthBadge({ method }: { method: 'email' | 'apple' | 'google' }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    email: { bg: 'var(--chip)', fg: 'var(--ink)' },
    apple: { bg: '#000', fg: '#fff' },
    google: { bg: 'rgba(66,133,244,0.12)', fg: '#1a73e8' },
  };
  const c = colors[method] ?? colors.email;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 100, background: c.bg, color: c.fg, fontSize: 12, fontWeight: 600 }}>
      {method}
    </span>
  );
}

function PlanBadge({ user }: { user: UserRow }) {
  const choice = currentChoice(user);
  const label = PLAN_LABELS[choice];
  const isPremium = choice !== 'free';
  return (
    <span
      style={{
        display: 'inline-block',
        background: isPremium ? 'rgba(232,154,110,0.18)' : 'var(--chip)',
        color: isPremium ? '#7b3614' : 'var(--ink-3)',
        borderRadius: 100,
        padding: '4px 12px',
        fontSize: 12,
        fontWeight: 600,
        fontFamily: 'var(--font-ui)',
      }}
    >
      {label}
    </span>
  );
}

function StatusBadge({ status }: { status: UserRow['status'] }) {
  const colors: Record<UserRow['status'], { bg: string; fg: string }> = {
    active: { bg: 'rgba(56,142,60,0.12)', fg: '#2e7d32' },
    suspended: { bg: 'rgba(208,64,48,0.12)', fg: '#a8331f' },
    soft_deleted: { bg: 'var(--chip)', fg: 'var(--ink-3)' },
    hard_deleted: { bg: 'var(--chip)', fg: 'var(--ink-3)' },
  };
  const c = colors[status] ?? colors.active;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 100, background: c.bg, color: c.fg, fontSize: 12, fontWeight: 600 }}>
      {status}
    </span>
  );
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Last seen: "● Online" when the user is currently heartbeating (global
// presence room), else the most recent disconnect date, else a dash.
function lastSeenLabel(user: UserRow) {
  if (user.online) {
    return <span style={{ color: '#2e7d32', fontWeight: 600 }}>● Online</span>;
  }
  return user.last_seen_at
    ? <>{formatDate(user.last_seen_at)}</>
    : <span style={{ color: 'var(--ink-3)' }}>—</span>;
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      className="mono"
      style={{
        textAlign: 'left',
        padding: '12px 14px',
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: 1,
        color: 'var(--ink-3)',
        fontWeight: 500,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <td
      className={mono ? 'mono' : undefined}
      style={{
        padding: '12px 14px',
        color: 'var(--ink)',
        fontSize: mono ? 13 : 14,
      }}
    >
      {children}
    </td>
  );
}

// ───── styles ─────────────────────────────────────────────────────────────

const emailLink: React.CSSProperties = {
  color: 'var(--ink)',
  textDecoration: 'none',
  borderBottom: '1px dashed var(--line-strong)',
};

const searchInputStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1.5px solid var(--line)',
  borderRadius: 10,
  padding: '8px 12px',
  fontSize: 13,
  fontFamily: 'var(--font-ui)',
  outline: 'none',
  width: 280,
  // Lets the field shrink below 280px on a phone instead of overflowing.
  minWidth: 0,
  maxWidth: '100%',
};

const searchBtn: React.CSSProperties = {
  background: 'var(--coral)',
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  padding: '8px 14px',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
};

const clearBtn: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--ink-3)',
  border: '1px solid var(--line)',
  borderRadius: 10,
  padding: '8px 14px',
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
};

const pageBtn: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--line)',
  borderRadius: 10,
  padding: '6px 12px',
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
  color: 'var(--ink)',
};

const emptyCell: React.CSSProperties = {
  padding: '24px 14px',
  textAlign: 'center',
  color: 'var(--ink-3)',
  fontSize: 14,
};

/**
 * Marks an API-created account in the console ONLY. Nothing equivalent
 * exists in the consumer app: to another user, a synthetic account is
 * indistinguishable from a real one, which is the point — they are meant
 * to behave like real profiles under test.
 */
const syntheticBadge: React.CSSProperties = {
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  padding: '2px 6px',
  borderRadius: 6,
  background: 'rgba(0,0,0,0.06)',
  color: '#555',
  whiteSpace: 'nowrap',
};
